import { useEffect } from "react";
import { ToolCard } from "./ToolCard.jsx";
import { renderFamilyGlyph } from "./toolGlyphs.jsx";

// V57.7 — FolderOverlay: when a FolderTile is tapped, this opens
// over the family-tile grid showing the tools inside that family.
// Backdrop is semi-transparent so the underlying grid is still
// visible at the edges. Tap backdrop or close button to dismiss.
//
// Browser back button closes the overlay via popstate — pushState
// is called on open, popstate listener closes on back.

export function FolderOverlay({ family, onClose }) {
  // Push a history entry on open so back button closes overlay.
  useEffect(() => {
    if (!family) return;
    window.history.pushState({ overlay: family.id }, "");

    const handler = (e) => {
      onClose();
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [family, onClose]);

  // Escape key also closes.
  useEffect(() => {
    if (!family) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        window.history.back();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [family]);

  // When user clicks Close or backdrop, fire history.back so popstate
  // handler runs (closes overlay AND removes the pushed state).
  const closeWithHistory = () => window.history.back();

  if (!family) return null;

  return (
    <div
      onClick={closeWithHistory}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(10, 11, 20, 0.78)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        paddingTop: "calc(20px + env(safe-area-inset-top))",
        paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
        animation: "folderOverlay-fadeIn 0.18s ease-out",
      }}
    >
      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${family.label} tools`}
        style={{
          background: "#13141F",
          border: `1px solid ${family.accent}44`,
          borderRadius: 22,
          padding: "22px 22px 26px",
          width: "100%",
          maxWidth: 720,
          maxHeight: "85vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
          animation: "folderOverlay-scaleIn 0.2s cubic-bezier(0.2, 0.7, 0.3, 1)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `linear-gradient(135deg, ${family.accent}, ${family.accentDeep})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff",
            }}>
              {renderFamilyGlyph(family.id, { size: 24, stroke: "#fff" })}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#E8E9F3", letterSpacing: "-0.2px" }}>
                {family.label}
              </div>
              <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {family.tools.length} {family.tools.length === 1 ? "tool" : "tools"}
              </div>
            </div>
          </div>
          <button
            onClick={closeWithHistory}
            aria-label="Close folder"
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: "#1C1E2E", border: "none",
              color: "#9A9DBE", fontSize: 18,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Tool tiles in a vertical-flowing grid (use existing ToolCard) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 12,
        }}>
          {family.tools.map((t) => (
            <ToolCard
              key={t.to}
              to={t.to}
              name={t.name}
              desc={t.desc}
              accent={family.accent}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes folderOverlay-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes folderOverlay-scaleIn {
          from { transform: scale(0.92); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
